import React, { useRef, useState, useEffect } from 'react';
import researchService from '../../services/researchService';
import EarningsReviewDialog from './EarningsReviewDialog';

const DOC_TYPE_OPTIONS = [
  { value: 'earnings_press_release', label: 'Earnings Press Release', description: 'Full extraction, trusted source — wins all conflicts' },
  { value: 'earnings_deck',          label: 'Earnings Deck',          description: 'Full extraction, supplemental — lower trust than press release' },
  { value: 'notes',                  label: 'Notes',                  description: 'Analyst notes, transcripts — indexed in KB only, no extraction' },
  { value: 'other',                  label: 'Other',                  description: 'Any other doc — indexed in KB only, no extraction' },
];

const EARNINGS_TYPES = new Set(['earnings_press_release', 'earnings_deck']);

/**
 * Upload panel for a single company, rendered as a modal.
 *
 * Flow:
 *   1. User fills in metadata (author, date, title) and picks a file — no doc_type selector yet.
 *   2. Upload button → presign + S3 PUT.
 *   3. POST /research/classify-doc → LLM suggests a doc type (spinner shown).
 *   4. Confirmation step: dropdown pre-populated with suggestion; user confirms or changes.
 *   5a. Earnings type → start async extraction → poll → EarningsReviewDialog → finalize.
 *   5b. Notes / Other → POST /research/upload/confirm → done.
 */
export default function ResearchUploadPanel({ companyId, onUploaded, show, onClose, replaceFileId, replaceFilename }) {
  const fileRef = useRef(null);
  const [author, setAuthor] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  // Classification / confirmation step
  const [classifyStep, setClassifyStep] = useState(false); // true = show doc-type confirmation
  const [classifyBusy, setClassifyBusy] = useState(false); // true = waiting for LLM
  const [classifyReasoning, setClassifyReasoning] = useState('');
  const [docType, setDocType] = useState('notes'); // final confirmed doc type
  const [includeInKnowledgeBase, setIncludeInKnowledgeBase] = useState(true);
  const [pendingFileId, setPendingFileId] = useState(null);
  const [targetFiscalYear, setTargetFiscalYear] = useState('');
  const [targetFiscalQuarter, setTargetFiscalQuarter] = useState('');

  // Earnings extraction state
  const [earningsPreview, setEarningsPreview] = useState(null);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [extractionWarning, setExtractionWarning] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    resetAll();
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetAll() {
    setAuthor('');
    setDocumentDate('');
    setTitle('');
    setProgress(null);
    setBusy(false);
    setError(null);
    setLastResult(null);
    setClassifyStep(false);
    setClassifyBusy(false);
    setClassifyReasoning('');
    setDocType('notes');
    setIncludeInKnowledgeBase(true);
    setPendingFileId(null);
    setTargetFiscalYear('');
    setTargetFiscalQuarter('');
    setEarningsPreview(null);
    setExtractionStatus(null);
    setExtractionWarning(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (fileRef.current) fileRef.current.value = '';
  }

  // Step 1: presign + S3 upload, then classify
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Please choose a file');
      return;
    }

    setBusy(true);
    setProgress(0);

    try {
      // Presign with a temporary doc_type of 'notes'; it will be updated after user confirms
      const presignBody = {
        company_id: companyId,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        doc_type: 'notes',
        author: author.trim() || undefined,
        document_date: documentDate || undefined,
        title: title.trim() || undefined,
      };

      const { data: presigned } = await researchService._rawPost('/research/upload/presigned', presignBody);
      if (!presigned.success || !presigned.upload_url) throw new Error(presigned.error || 'Failed to get presigned URL');

      await researchService._s3Put(presigned.upload_url, file, presignBody.content_type, setProgress);
      setProgress(null);

      // Step 2: classify
      setClassifyBusy(true);
      let suggestion = 'notes';
      let reasoning = '';
      try {
        const classifyResult = await researchService.classifyDoc(presigned.file_id);
        suggestion = classifyResult.suggested_doc_type || 'notes';
        reasoning = classifyResult.reasoning || '';
        if (classifyResult.suggested_fiscal_year) setTargetFiscalYear(String(classifyResult.suggested_fiscal_year));
        if (classifyResult.suggested_fiscal_quarter) setTargetFiscalQuarter(String(classifyResult.suggested_fiscal_quarter));
      } catch {
        reasoning = 'Could not classify; please select manually.';
      }

      setPendingFileId(presigned.file_id);
      setDocType(suggestion);
      setClassifyReasoning(reasoning);
      setClassifyStep(true);
      setClassifyBusy(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  // ── no handleReExtract — Re-extract button removed

  // Step 2: user has confirmed the doc type
  const handleConfirmType = async () => {
    setError(null);
    setBusy(true);
    setClassifyStep(false);

    try {
      if (includeInKnowledgeBase && EARNINGS_TYPES.has(docType)) {
        // Earnings: start async extraction
        setExtractionStatus('Extracting earnings data with AI...');
        const jobStart = await researchService.startEarningsPreview(pendingFileId, {
          docType,
          targetFiscalYear: targetFiscalYear ? parseInt(targetFiscalYear, 10) : null,
          targetFiscalQuarter: targetFiscalQuarter ? parseInt(targetFiscalQuarter, 10) : null,
        });

        await new Promise((resolve, reject) => {
          pollRef.current = setInterval(async () => {
            try {
              const poll = await researchService.pollEarningsPreview(jobStart.file_id);
              if (poll.status === 'completed') {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setExtractionStatus(null);
                setEarningsPreview(poll);
                if (poll.custom_metrics_warning) {
                  setExtractionWarning(poll.custom_metrics_warning);
                }
                resolve();
              } else if (poll.status === 'failed') {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setExtractionStatus(null);
                reject(new Error(poll.error || 'Extraction failed'));
              }
            } catch (pollErr) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setExtractionStatus(null);
              reject(pollErr);
            }
          }, 4000);
        });
      } else {
        // Non-earnings or explicit no-KB: finalize tracking without extraction
        const result = await researchService.confirmUpload({
          file_id: pendingFileId,
          replace_file_id: replaceFileId || undefined,
          doc_type: docType,
          include_in_knowledge_base: includeInKnowledgeBase,
        });
        setLastResult(result);
        if (onUploaded) onUploaded(result);
        if (fileRef.current) fileRef.current.value = '';
        setTitle('');
        setTimeout(() => { if (onClose) onClose(); }, 1000);
      }
    } catch (err) {
      setExtractionStatus(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setError(err?.response?.data?.detail || err.message || 'Processing failed');
    } finally {
      if (!includeInKnowledgeBase || !EARNINGS_TYPES.has(docType) || error) setBusy(false);
    }
  };

  const handleReviewConfirm = async (confirmedPeriods) => {
    try {
      const result = await researchService.finalizeEarningsUpload(pendingFileId, confirmedPeriods, docType);
      setLastResult(result);
      setEarningsPreview(null);
      setExtractionStatus(null);
      setPendingFileId(null);
      setBusy(false);
      if (onUploaded) onUploaded(result);
      if (fileRef.current) fileRef.current.value = '';
      setTitle('');
      setTimeout(() => { if (onClose) onClose(); }, 1000);
    } catch (err) {
      setEarningsPreview(null);
      setExtractionStatus(null);
      setPendingFileId(null);
      setBusy(false);
      setError(err?.response?.data?.detail || err.message || 'Finalize failed');
    }
  };

  const _deletePendingFile = (fileId) => {
    if (!fileId) return;
    researchService.deleteFile(fileId).catch(() => {/* best-effort */});
  };

  const handleClose = () => {
    _deletePendingFile(pendingFileId);
    if (onClose) onClose();
  };

  const handleReviewCancel = () => {
    _deletePendingFile(pendingFileId);
    setEarningsPreview(null);
    setExtractionStatus(null);
    setPendingFileId(null);
    setBusy(false);
    setError('Upload cancelled \u2014 earnings were extracted but not saved.');
  };

  if (!show) return null;

  return (
    <>
      {earningsPreview && (
        <>
          {extractionWarning && (
            <div
              className="alert alert-warning mb-0"
              style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1070, maxWidth: 600 }}
            >
              <i className="bi bi-exclamation-triangle me-2" />
              {extractionWarning}
            </div>
          )}
          <EarningsReviewDialog
            preview={earningsPreview}
            fileId={pendingFileId}
            companyId={companyId}
            onConfirm={handleReviewConfirm}
            onCancel={handleReviewCancel}
          />
        </>
      )}

      <div className="modal-backdrop fade show" onClick={!busy ? handleClose : undefined} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-nowrap">
                {replaceFileId
                  ? <><i className="bi bi-arrow-repeat me-2" />Replace file</>
                  : <><i className="bi bi-cloud-upload me-2" />Upload data</>}
              </h5>
              {replaceFileId && replaceFilename && (
                <div className="ms-4">
                  <small className="text-muted">Replacing: <strong>{replaceFilename}</strong></small>
                </div>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                disabled={busy}
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              {/* Step 1: metadata + file */}
              {!classifyStep && !classifyBusy && (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Author</label>
                      <input
                        className="form-control"
                        placeholder="(defaults to company or you)"
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Document date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={documentDate}
                        onChange={(e) => setDocumentDate(e.target.value)}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Title (optional)</label>
                      <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>

                    <div className="col-12">
                      <label className="form-label">File</label>
                      <input ref={fileRef} type="file" className="form-control" />
                    </div>
                  </div>

                  {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
                  {lastResult && (
                    <div className="alert alert-success mt-3 mb-0">
                      Uploaded. Status: <strong>{lastResult.status}</strong>
                    </div>
                  )}

                  <div className="modal-footer px-0 pb-0 mt-3">
                    <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={busy || !companyId}>
                      {busy && progress !== null ? `Uploading... ${progress}%` : 'Upload'}
                    </button>
                  </div>
                </form>
              )}

              {/* Classify spinner */}
              {classifyBusy && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <p className="mb-0 text-muted">Analyzing document type...</p>
                </div>
              )}

              {/* Step 2: doc type confirmation */}
              {classifyStep && !classifyBusy && (
                <div>
                  <p className="mb-3">
                    <i className="bi bi-robot me-2 text-primary" />
                    AI has suggested a document type. Confirm or change before processing.
                  </p>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Document type <span className="text-muted fw-normal">(AI suggestion)</span>
                    </label>
                    <select
                      className="form-select"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                    >
                      {DOC_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {classifyReasoning && (
                      <div className="form-text fst-italic text-muted mt-1">{classifyReasoning}</div>
                    )}
                    <div className="form-text mt-1">
                      {DOC_TYPE_OPTIONS.find(o => o.value === docType)?.description || ''}
                    </div>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      id="include-in-kb-check"
                      className="form-check-input"
                      type="checkbox"
                      checked={includeInKnowledgeBase}
                      onChange={(e) => setIncludeInKnowledgeBase(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="include-in-kb-check">
                      Ingest this file into the knowledge base
                    </label>
                    <div className="form-text mt-1">
                      If unchecked, the file is still stored and tracked in S3, but moved to a non-KB folder and skipped for extraction/indexing.
                    </div>
                  </div>

                  {includeInKnowledgeBase && EARNINGS_TYPES.has(docType) && (
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          Fiscal year <span className="text-muted fw-normal">(AI suggestion)</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="e.g. 2026"
                          value={targetFiscalYear}
                          onChange={(e) => setTargetFiscalYear(e.target.value)}
                          min="2000"
                          max="2099"
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          Fiscal quarter <span className="text-muted fw-normal">(AI suggestion)</span>
                        </label>
                        <select
                          className="form-select"
                          value={targetFiscalQuarter}
                          onChange={(e) => setTargetFiscalQuarter(e.target.value)}
                        >
                          <option value="">Annual / unknown</option>
                          <option value="1">Q1</option>
                          <option value="2">Q2</option>
                          <option value="3">Q3</option>
                          <option value="4">Q4</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <div className="form-text text-muted">
                          The extraction AI will be told to target this specific period column in the document.
                        </div>
                      </div>
                    </div>
                  )}

                  {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}

                  <div className="d-flex gap-2 justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { setClassifyStep(false); setPendingFileId(null); }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConfirmType}
                      disabled={busy}
                    >
                      {busy
                        ? extractionStatus || 'Processing...'
                        : includeInKnowledgeBase && EARNINGS_TYPES.has(docType)
                          ? 'Confirm & Extract'
                          : includeInKnowledgeBase
                            ? 'Confirm & Save'
                            : 'Confirm & Save (No KB)'}
                    </button>
                  </div>
                </div>
              )}

              {/* Extraction spinner (after type confirmed, before review dialog) */}
              {!classifyStep && !classifyBusy && extractionStatus && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <p className="mb-0 text-muted">{extractionStatus}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
